// Utility functions for package data processing
import { Package } from '../config/api'

// Common destinations and countries that appear in package titles
const COMMON_DESTINATIONS = [
  // African destinations
  'Mauritius', 'Maldives', 'Seychelles', 'Zanzibar', 'Kenya', 'Tanzania',
  'South Africa', 'Cape Town', 'Durban', 'Johannesburg', 'Kruger',
  'Botswana', 'Namibia', 'Zimbabwe', 'Victoria Falls', 'Zambia',
  'Morocco', 'Egypt', 'Tunisia',
  
  // Asian destinations
  'Thailand', 'Bangkok', 'Phuket', 'Bali', 'Indonesia', 'Vietnam',
  'Singapore', 'Malaysia', 'Philippines', 'Japan', 'China', 'Hong Kong',
  'India', 'Sri Lanka', 'Nepal', 'Cambodia', 'Laos',
  
  // Middle Eastern destinations
  'Dubai', 'Abu Dhabi', 'UAE', 'Qatar', 'Oman', 'Jordan', 'Turkey', 'Istanbul',
  
  // European destinations
  'Europe', 'Greece', 'Italy', 'Rome', 'Venice', 'Florence', 'Spain',
  'Barcelona', 'Madrid', 'France', 'Paris', 'London', 'United Kingdom',
  'Portugal', 'Lisbon', 'Amsterdam', 'Netherlands', 'Switzerland',
  'Germany', 'Austria', 'Iceland', 'Norway', 'Sweden', 'Denmark',
  'Croatia', 'Santorini', 'Mykonos', 'Athens',
  
  // American destinations
  'USA', 'United States', 'New York', 'California', 'Florida', 'Miami',
  'Las Vegas', 'Hawaii', 'Canada', 'Mexico', 'Cancun', 'Brazil',
  'Argentina', 'Chile', 'Peru', 'Costa Rica',
  
  // Oceania
  'Australia', 'Sydney', 'Melbourne', 'New Zealand', 'Fiji', 'Tahiti',
  'Reunion Island', 'Réunion',
]

/**
 * Extracts destination/country from package title
 * Looks for common destination names in the title string
 */
export function extractDestinationFromTitle(title: string): string | null {
  if (!title) return null
  
  // Create a case-insensitive search
  const titleLower = title.toLowerCase()
  
  // Sort destinations by length (longest first) to match more specific names first
  // e.g., "South Africa" before "Africa"
  const sortedDestinations = [...COMMON_DESTINATIONS].sort((a, b) => b.length - a.length)
  
  for (const destination of sortedDestinations) {
    const destLower = destination.toLowerCase()
    
    // Check if the destination name appears as a whole word
    // Use word boundaries to avoid partial matches
    const regex = new RegExp(`\\b${destLower}\\b`, 'i')
    if (regex.test(titleLower)) {
      return destination
    }
  }
  
  return null
}

/**
 * Gets the best available destination/country for a package
 * Priority:
 * 1. Extract from title (most reliable)
 * 2. Use destination.name from API
 * 3. Use location from API
 * 4. Default to 'International'
 */
export function getPackageDestination(pkg: any): string {
  // First, try to extract from title - this is often most accurate
  const titleDestination = extractDestinationFromTitle(pkg.name)
  if (titleDestination) {
    return titleDestination
  }
  
  // Fall back to API-provided data
  if (pkg.destination?.name) {
    return pkg.destination.name
  }
  
  if (pkg.location) {
    return pkg.location
  }
  
  // Try to extract from description if title didn't work
  const descDestination = extractDestinationFromTitle(pkg.intro || pkg.description)
  if (descDestination) {
    return descDestination
  }
  
  return 'International'
}

/**
 * Alternative function that prioritizes API data over extraction
 * Use this if you trust the API data more than title extraction
 */
export function getPackageDestinationApiFirst(pkg: any): string {
  // First check API data
  if (pkg.destination?.name && pkg.destination.name !== 'International') {
    return pkg.destination.name
  }
  
  if (pkg.location && pkg.location !== 'International') {
    return pkg.location
  }
  
  // Then try to extract from title
  const titleDestination = extractDestinationFromTitle(pkg.name)
  if (titleDestination) {
    return titleDestination
  }
  
  // Try description as last resort
  const descDestination = extractDestinationFromTitle(pkg.intro || pkg.description)
  if (descDestination) {
    return descDestination
  }
  
  return 'International'
}

export function getPackageImageUrl(pkg: Package | Record<string, any> | null | undefined): string | null {
  if (!pkg) return null
  if (pkg.cover_photo?.large_url) return pkg.cover_photo.large_url
  if (pkg.cover_photo?.url) return pkg.cover_photo.url
  if (pkg.scraped_cover_photo) return pkg.scraped_cover_photo
  return null
}

export function formatTravelDates(
  travelFrom?: string,
  travelTo?: string,
  fallback?: string
): string {
  if (!travelFrom || !travelTo) {
    return fallback || 'Enquire for dates'
  }

  const start = new Date(travelFrom)
  const end = new Date(travelTo)

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return fallback || `${travelFrom} - ${travelTo}`
  }

  const day = (date: Date) => date.getDate().toString()
  const month = (date: Date) => date.toLocaleDateString('en-GB', { month: 'short' })
  const year = (date: Date) => date.getFullYear()

  const sameYear = start.getFullYear() === end.getFullYear()
  const sameMonth = sameYear && start.getMonth() === end.getMonth()

  if (sameMonth) {
    return `${day(start)} — ${day(end)} ${month(end)} ${year(end)}`
  }

  if (sameYear) {
    return `${day(start)} ${month(start)} — ${day(end)} ${month(end)} ${year(end)}`
  }

  return `${day(start)} ${month(start)} ${year(start)} — ${day(end)} ${month(end)} ${year(end)}`
}

export function getPackageIncludesLabel(pkg: Package | Record<string, any>): string {
  const parts: string[] = []
  if (pkg.flight_included) parts.push('Flights')
  if (pkg.hotel_included) parts.push('Hotel')
  if (pkg.transfer_included) parts.push('Transfers')
  return parts.length ? parts.join(' + ') : 'Package'
}

export const PACKAGE_CARD_GRADIENTS = [
  'linear-gradient(135deg,#3F7A4E,#1F4A34)',
  'linear-gradient(135deg,#3E6E8E,#1F4A34)',
  'linear-gradient(135deg,#D98E2B,#B96E17)',
  'linear-gradient(135deg,#3F7A4E,#1F4A34)',
]

export const DESTINATION_GRADIENTS = [
  'linear-gradient(160deg,#D98E2B,#1F4A34)',
  'linear-gradient(160deg,#3E6E8E,#1F4A34)',
  'linear-gradient(160deg,#B96E17,#3F7A4E)',
  'linear-gradient(160deg,#3F7A4E,#1F4A34)',
]

// Country destinations whose packages are tagged under more specific child destinations.
export const DESTINATION_GROUPS: Record<number, number[]> = {
  // South Africa: Cape Town, Kruger, Garden Route, Western Cape, Johannesburg, Eastern Cape, etc.
  97: [97, 21, 52, 5, 16, 189, 3, 48, 30, 51, 41, 12, 35, 183, 369, 584],
  // France: Paris, Disneyland Paris, and other France-tagged destinations
  47: [47, 367, 642, 695, 702, 708, 751, 849, 1143],
}

export function expandDestinationIds(destinationId: number | string): number[] {
  const id = typeof destinationId === 'string' ? parseInt(destinationId, 10) : destinationId
  if (!id || Number.isNaN(id)) return []
  const grouped = DESTINATION_GROUPS[id]
  return grouped ? [...new Set(grouped)] : [id]
}

